import React from "react";
import { AlertTriangle, ExternalLink } from "lucide-react";

/**
 * Banner that surfaces only when the user has filtered the registry to reptiles
 * (filters.q === "reptile") OR turtles. Reptile-specific cruelty convictions
 * are rare in public court records because most state statutes narrowly define
 * "companion animal" as dogs and cats. We acknowledge that gap honestly and
 * redirect the user to advocacy resources instead of silently looking thin.
 */
export default function ReptileProtectionGap({ filters }) {
  const q = (filters?.q || "").toLowerCase();
  const isReptileScope = q === "reptile" || q === "turtle";
  if (!isReptileScope) return null;

  const isTurtle = q === "turtle";

  return (
    <div
      className="mt-4 border border-amber-400 bg-amber-50 p-5"
      data-testid="reptile-protection-gap-banner"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-9 h-9 border border-amber-600 bg-amber-600 text-white flex items-center justify-center">
          <AlertTriangle size={18} />
        </div>
        <div className="flex-1">
          <div className="label-eyebrow text-amber-800">
            {isTurtle ? "Turtle protection gap" : "Reptile protection gap"}
          </div>
          <h3 className="font-display font-black text-xl tracking-tight mt-1 leading-snug">
            {isTurtle
              ? "Turtles are mostly protected under federal trafficking law, not state cruelty law."
              : "Reptiles fall through the cracks of most state cruelty statutes."}
          </h3>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            {isTurtle
              ? "The turtle convictions shown here are almost all federal Lacey Act (16 U.S.C. § 3372) prosecutions for illegal trafficking — not state-level cruelty convictions. Most state cruelty statutes narrowly define 'companion animal' as dogs and cats, leaving turtles with little legal protection as individual victims."
              : "Most state cruelty statutes define 'companion animal' narrowly enough to exclude reptiles. That's why this registry shows only a handful of reptile cases — not because abuse is rare, but because prosecutors rarely have a felony charge available when the victim is a snake, lizard, or turtle. Florida (FWC) and a handful of federal Lacey Act prosecutions are the primary enforcement paths today."}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="https://aldf.org/project/us-state-rankings/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border border-amber-700 bg-white text-amber-900 hover:bg-amber-700 hover:text-white transition-colors"
              data-testid="reptile-gap-aldf-link"
            >
              <ExternalLink size={11} /> ALDF state rankings
            </a>
            <a
              href="https://www.animallaw.info/topic/detailed-discussion-reptiles"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border border-amber-700 bg-white text-amber-900 hover:bg-amber-700 hover:text-white transition-colors"
              data-testid="reptile-gap-alhc-link"
            >
              <ExternalLink size={11} /> Reptile-law overview
            </a>
            <a
              href="https://myfwc.com/wildlifehabitats/wildlife/captive/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border border-amber-700 bg-white text-amber-900 hover:bg-amber-700 hover:text-white transition-colors"
              data-testid="reptile-gap-fwc-link"
            >
              <ExternalLink size={11} /> Report in FL (FWC)
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
